/*
 * Formatarea pentru ecran. Fara nicio dependinta si fara nicio conversie care
 * schimba valoarea.
 */

import type { CostPeriod, CostType, Money, Pricing, PricingComponent } from '../api/types';

const dateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const EM_DASH = '—';

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const date = new Date(iso);
  /* O data pe care browserul n-o intelege se arata ca text brut, nu ca
     "Invalid Date": asa se vede ca api-ul trimite alt format, nu ca lipseste. */
  if (Number.isNaN(date.getTime())) return iso;
  return dateTimeFormat.format(date);
}

/**
 * Banii: gruparea miilor se face prin taiere de sir, nu prin `Number`.
 *
 * Contractul interzice explicit float-ul. O rotunjire aparuta doar la afisare e
 * la fel de gresita ca una din calcul: omul citeste alt pret decat cel care se
 * factureaza, si nu are cum sa-si dea seama.
 */
export function formatMoney(money: Money | null | undefined): string {
  if (!money || money.amount === null || money.amount === undefined) return EM_DASH;

  let text = String(money.amount).trim();
  let sign = '';
  if (text.startsWith('-') || text.startsWith('+')) {
    sign = text.startsWith('-') ? '-' : '';
    text = text.slice(1);
  }

  const dot = text.indexOf('.');
  const whole = dot === -1 ? text : text.slice(0, dot);
  const fraction = dot === -1 ? '' : text.slice(dot);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${sign}${grouped}${fraction}${money.currency ? ` ${money.currency}` : ''}`;
}

/**
 * Titlul unei stari tehnice.
 *
 * O stare necunoscuta se afiseaza ca atare, cu underscore inlocuit, nu se
 * ascunde: cand api-ul adauga o stare noua, ea trebuie sa apara pe ecran, nu sa
 * lase o eticheta goala pe care nimeni n-o observa.
 */
export function labelState(state: string | null | undefined): string {
  if (!state) return 'Unknown';
  const text = String(state).replace(/[_-]+/g, ' ').trim();
  if (!text) return 'Unknown';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** Numele unei persoane poate veni ca sir sau ca obiect; ambele forme se accepta. */
export function personName(
  person: { name?: string; email?: string } | string | null | undefined,
): string {
  if (!person) return '';
  if (typeof person === 'string') return person;
  return person.name ?? person.email ?? '';
}

/** Lista de titluri, scrisa pentru citit: "A, B and C". */
export function joinTitles(titles: string[]): string {
  if (titles.length === 0) return '';
  if (titles.length === 1) return titles[0] ?? '';
  return `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
}

/*
 * Pretul - §2 din contract. Api-ul trimite sloguri stabile (`kind`, `type`,
 * `period`); traducerea catre text de ecran se face aici, in pagina, si numai
 * aici. Api-ul nu trimite niciodata etichete gata traduse.
 */

const costTypeLabels: Record<CostType, string> = {
  fixed: 'Fixed',
  per_ci: 'Per item',
  per_user: 'Per user',
  per_ticket: 'Per ticket',
};

/** Sufixul scurt de langa cifra pe card: "18000 RON /mo". */
const periodSuffixes: Record<CostPeriod, string> = {
  monthly: '/mo',
  yearly: '/yr',
};

/** Perioada scrisa lung, pentru fisa: "per month". */
const periodLongLabels: Record<CostPeriod, string> = {
  monthly: 'per month',
  yearly: 'per year',
};

export function costTypeLabel(type: string): string {
  return costTypeLabels[type as CostType] ?? labelState(type);
}

export function periodSuffix(period: string): string {
  return periodSuffixes[period as CostPeriod] ?? '';
}

export function periodLongLabel(period: string): string {
  return periodLongLabels[period as CostPeriod] ?? '';
}

/**
 * Linia scurta de pret pentru card. O singura propozitie, pe `kind`:
 *   free     -> "No charge"        (chiar nu costa)
 *   variable -> "Variable"         (costa, dar nu exista o cifra unica)
 *   fixed    -> "18000 RON /mo"
 *   mixed    -> "18000 RON /mo + usage" (baza fixa plus consum)
 *
 * Doar `free` scrie "No charge"; asta era greseala veche, cand orice pret lipsa
 * devenea "No charge".
 */
export function pricingHeadline(pricing: Pricing | null | undefined): string {
  if (!pricing || pricing.kind === 'free') return 'No charge';
  if (pricing.kind === 'variable') return 'Variable';

  const base = pricing.fixed
    ? `${formatMoney(pricing.fixed)}${periodSuffix(pricing.fixed.period)}`
    : 'Variable';

  return pricing.kind === 'mixed' ? `${base} + usage` : base;
}

/** O componenta de pret scrisa pe o linie pentru fisa: "Per item — 450 RON per month". */
export function componentLine(component: PricingComponent): string {
  const label = costTypeLabel(component.type);
  const money = formatMoney(component.unit);
  const period = periodLongLabel(component.period);
  return period ? `${label} ${EM_DASH} ${money} ${period}` : `${label} ${EM_DASH} ${money}`;
}

/**
 * Marimea unui fisier, in cuvintele omului.
 *
 * Fara zecimale sub un megaoctet: "512 KB" spune tot, iar "512,4 KB" adauga o
 * cifra pe care nimeni n-o foloseste. De la un megaoctet in sus se pastreaza
 * una singura, fiindca acolo diferenta dintre 1 si 1,8 chiar conteaza cand
 * limita e zece.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';

  const kb = bytes / 1024;

  if (kb < 1024) return Math.max(1, Math.round(kb)) + ' KB';

  return (Math.round((kb / 1024) * 10) / 10).toString().replace('.', ',') + ' MB';
}
